/**
 * Who these ids are: the name shown on the row, and one word describing them
 * — a colleague's role, or the class a pupil (or their parent) belongs to.
 * Read once and kept, so the list does not hit Firestore per row.
 */
export async function loadPeopleInfo(school, year, ids) {
  const info = {};
  const wanted = [...new Set(ids.filter(Boolean))];
  if (!wanted.length) return info;

  const staffSnap = await school.ref.collection("teachers").get();
  staffSnap.forEach((doc) => {
    if (!wanted.includes(doc.id)) return;
    const data = doc.data() || {};
    info[doc.id] = {
      name: staffName(data) || "Ажилтан",
      role: String(data.role || "").toLowerCase(),
      label: roleLabel(String(data.role || "").toLowerCase()),
      photo: data.photoURL || "",
    };
  });

  const studentIds = wanted
    .filter((id) => id.startsWith("parent_"))
    .map((id) => id.replace("parent_", ""));
  const others = wanted.filter((id) => !info[id] && !id.startsWith("parent_"));
  const lookFor = [...new Set([...others, ...studentIds])];

  const unresolved = await _readStudents(school, year, lookFor, info);
  // Хичээлийн жил солигдоход хуучин чатын сурагч өмнөх жилийн бүртгэлд
  // үлддэг. Олдоогүй хүн байвал л хөрш жилүүдийг нэмж асууна.
  if (unresolved.length) {
    const rest = await _readStudents(school, year - 1, unresolved, info);
    if (rest.length) await _readStudents(school, year + 1, rest, info);
  }

  return info;
}

/** Уншаад олдоогүй id-уудыг буцаана. */
async function _readStudents(school, year, ids, info) {
  if (!ids.length) return [];
  const found = new Set();

  for (let i = 0; i < ids.length; i += 10) {
    const batch = ids.slice(i, i + 10);
    if (!batch.length) continue;

    let snap = null;
    try {
      snap = await school.ref
        .collection(`students-${year}`)
        .where(fb.firestore.FieldPath.documentId(), "in", batch)
        .get();
    } catch (e) {
      continue;
    }

    snap.forEach((doc) => {
      const data = doc.data() || {};
      const name = studentName(data);
      const group = data.STUDENT_GROUP_NAME || "";
      found.add(doc.id);

      info[doc.id] = {
        name: name,
        label: group,
        photo: data.photoURL || "",
      };
      // A parent shares the pupil's document, so their picture is kept apart.
      info[`parent_${doc.id}`] = {
        name: `${name} — эцэг эх`,
        label: group,
        photo: data.PARENT_PHOTO_URL || "",
      };
    });
  }

  return ids.filter((id) => !found.has(id));
}

// Chat data layer, shared with the mobile app.
//
// Both apps read and write exactly the same documents:
//
//   schools/{schoolId}/chatRooms/{roomId}
//     type          "direct" | "group"
//     userType      "student" | "parent" | "teacher"   (which tab it belongs to)
//     participants  [userId]                            (teacher/student doc id,
//                                                        a parent is "parent_<studentId>")
//     name, lastMessage, lastMessageTime, lastMessageSender
//     admins, createdBy, createdAt, deleted
//
//   schools/{schoolId}/chatRooms/{roomId}/messages/{messageId}
//     senderId, senderName, content, type, timestamp
//     readBy { userId: true }, readAt { userId: timestamp }
//
// Nothing here is web-only: keep it that way, or the two apps drift apart.

import Vue from "vue";
import { store } from "@/store";

const fb = require("@/firebaseConfig.js");

/**
 * Colour of an avatar that has no picture. The mobile app repeats this list
 * and this hash (lib/configs/values.dart), so a person looks the same in both.
 */
const AVATAR_COLORS = [
  "#e17076",
  "#faa774",
  "#a695e7",
  "#7bc862",
  "#6ec9cb",
  "#65aadd",
  "#ee7aae",
];

export function avatarColor(key) {
  const text = String(key || "");
  if (text === "") return AVATAR_COLORS[0];

  let hash = 0;
  for (let i = 0; i < text.length; i++) {
    hash = (hash * 31 + text.charCodeAt(i)) & 0x7fffffff;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

/** The circle that stands for a whole room: the group, or the other person. */
export function roomAvatarColor(room, myId) {
  if (!room) return AVATAR_COLORS[0];
  if (room.type === "group") return avatarColor(room.id);

  const other = (room.participants || []).find((id) => id !== myId);
  return avatarColor(other || room.id);
}

/** Lets the top bar talk to the chat dock: "toggle" and "unread". */
export const chatBus = new Vue();

/**
 * Who has a chat at all. A super admin only borrows a school to look at it —
 * they are in nobody's staff list, so a room of theirs would belong to no one.
 */
export function canUseChat(userData) {
  if (!userData || !userData.school) return false;
  const role = String(userData.role || "").toLowerCase();
  return role !== "superadmin" && role !== "superadmin2";
}

// Poll colours live with the app's own settings so both clients read one
// place: _bagshSystemConfigurations/bagshMobileApp → pollColors.
// Read once per page load; a failure just leaves the built-in colours.
let pollColorsOnce = null;

export function pollColors() {
  if (!pollColorsOnce) {
    pollColorsOnce = fb.db
      .doc("_bagshSystemConfigurations/bagshMobileApp")
      .get()
      .then((doc) => doc.data() || {})
      .catch(() => ({}));
  }
  return pollColorsOnce;
}

/// Санал асуулга өөрийн өнгийг мессеж дээрээ авч явдаг. Өнгөгүй нь чатны
/// ердийн бөмбөлгийн өнгөтэй — аппын pollBackgroundOf / pollTextColorOf.
export function pollBackgroundOf(poll) {
  return hexColor(poll && poll.bg) || "#F0F0F0";
}

export function pollTextColorOf(poll) {
  return hexColor(poll && poll.text) || "#1F2937";
}

function hexColor(value) {
  const digits = String(value || "")
    .replace("#", "")
    .trim();
  if (digits.length !== 6 && digits.length !== 8) return null;
  if (!/^[0-9a-fA-F]+$/.test(digits)) return null;
  return digits.length === 6 ? `#${digits}` : `#${digits.slice(2)}`;
}

/// Өнгөний сонголтууд — аппын AppController.pollColorPresets-тэй ижил.
/// Хамгийн эхнийх нь өнгөгүй: чатны бөмбөлгийн өнгөөр гарна.
export async function pollColorPresets() {
  const data = await pollColors();
  const raw = Array.isArray(data.pollColors) ? data.pollColors : [];
  return [
    { bg: "", text: "" },
    ...raw
      .filter((c) => c && hexColor(c.bg) && hexColor(c.text))
      .map((c) => ({ bg: String(c.bg).trim(), text: String(c.text).trim() })),
  ];
}

function roomsCol(school) {
  return school.ref.collection("chatRooms");
}

/** Улсын чат (grp_all_*) root `chatRooms`-д, бусад нь сургуулийнхад. */
export function isNationalId(roomId) {
  return String(roomId || "").startsWith("grp_all_");
}

function roomRef(school, roomId) {
  return isNationalId(roomId)
    ? fb.db.collection("chatRooms").doc(roomId)
    : roomsCol(school).doc(roomId);
}

function messagesCol(school, roomId) {
  return roomRef(school, roomId).collection("messages");
}

// ── Rooms ───────────────────────────────────────────────────────────────────

/** Live list of the rooms this person takes part in, newest activity first. */
export function watchMyRooms(school, myId, onChange) {
  // Аппын ChatService.isPastYearClassRoom-той ижил дүрэм: өнгөрсөн жилийн
  // ангийн чат нуугдана, сургууль өөрөө асаавал харагдана. lessonYear-гүй
  // өрөө хэзээ ч нуугдахгүй.
  const showPastYear =
    (((school || {}).schoolDatas || {}).showPastYearClassChats) === true;
  const currentYear = Number(store.state.kSchoolStartYear);

  // Two sources, one list: the school's own rooms and the root national
  // rooms (grp_all_*) this person is in. Either snapshot re-emits the merge.
  let schoolRooms = [];
  let nationalRooms = [];
  const emit = () => {
    const rooms = [...schoolRooms, ...nationalRooms];
    rooms.sort((a, b) => millis(b.lastMessageTime) - millis(a.lastMessageTime));
    onChange(rooms);
  };
  const toRoom = (doc) => {
    const data = doc.data() || {};
    if (data.deleted === true) return null;
    if (!showPastYear && data.lessonYear && data.lessonYear !== currentYear)
      return null;
    // The document's own fields go first on purpose: the mobile app
    // writes an empty `id` field into the room it creates, and spreading
    // that over doc.id left every class chat unopenable here.
    return { ...data, id: doc.id, ref: doc.ref };
  };

  const stopSchool = roomsCol(school)
    .where("participants", "array-contains", myId)
    .onSnapshot((snap) => {
      schoolRooms = snap.docs.map(toRoom).filter(Boolean);
      emit();
    });
  const stopNational = fb.db
    .collection("chatRooms")
    .where("participants", "array-contains", myId)
    .where("groupSettings.autoKind", "==", "national")
    .onSnapshot(
      (snap) => {
        nationalRooms = snap.docs.map(toRoom).filter(Boolean);
        emit();
      },
      (e) => console.log("national rooms", e),
    );
  return () => {
    stopSchool();
    stopNational();
  };
}

/** Live messages of one room, oldest first. */
/**
 * Группын гишүүн чатаа нээхэд өөрийн мөрөө members дэд цуглуулгад бичнэ.
 * Аймгийн чаттай нэг зан төлөв — нэр, албан тушаал өрөөний массивд биш,
 * тус тусын баримтад амьдарна.
 */
export async function touchMyMember(school, room, userData) {
  if (!school || !school.ref || !room || !room.id) return;
  if (room.type === "direct" || isNationalId(room.id)) return;
  const myId = String(userData.id || "");
  if (!myId) return;
  if (!(room.participants || []).includes(myId)) return;
  try {
    await roomRef(school, room.id)
      .collection("members")
      .doc(myId)
      .set(
        {
          id: myId,
          name: staffName(userData),
          role: String(userData.role || ""),
          roleLabel: roleLabel(userData.role),
          school: school.name || "",
          schoolId: school.ref.id,
          schoolPath: school.ref.path,
          schoolRef: school.ref,
          userRef: userData.ref || null,
          userPath: userData.ref ? userData.ref.path : "",
          personId: userData.PERSON_ID || myId,
          isGlobal: false,
        },
        { merge: true },
      );
  } catch (error) {
    console.log("touchMyMember", error);
  }
}

export function watchMessages(school, roomId, myId, onChange) {
  return messagesCol(school, roomId)
    .orderBy("timestamp", "desc")
    .limit(200)
    .onSnapshot((snap) => {
      const messages = [];
      snap.forEach((doc) => {
        const data = doc.data() || {};

        if (data.hidden === true) return;

        // The mobile app writes notes meant for one member only.
        if (data.senderId === "system") {
          if (data.targetUserId && data.targetUserId !== myId) return;
          if (data.excludeUserId && data.excludeUserId === myId) return;
        }
        messages.push({ ...data, id: doc.id });
      });

      messages.reverse();
      onChange(messages);
    });
}

export async function sendMessage(school, room, me, text, replyToId, mentions) {
  const content = String(text || "").trim();
  if (content === "") return;

  const message = await messagesCol(school, room.id).add({
    chatRoomId: room.id,
    senderId: me.id,
    senderName: me.name,
    content: content,
    type: "text",
    ...(replyToId ? { replyToId } : {}),
    ...(mentions && mentions.length ? { mentions } : {}),
    timestamp: fb.firestore.FieldValue.serverTimestamp(),
    readBy: { [me.id]: true },
    isEdited: false,
    isDeleted: false,
    delivered: true,
  });

  await roomRef(school, room.id).update({
    lastMessage: content,
    lastMessageTime: fb.firestore.FieldValue.serverTimestamp(),
    lastMessageSender: me.id,
    // Compared with lastReadBy to know whether a room is unread, without
    // reading its messages. The mobile app writes it too.
    lastMessageId: message.id,
  });
}

// ── Polls ───────────────────────────────────────────────────────────────────
//
// A poll is an ordinary message of type "poll"; the answers people picked live
// in messages/{id}/votes/{userId}. The mobile app writes the same shape
// (lib/pages_v2/chat_v2/poll.dart).

/**
 * Зураг илгээх. Аппын ChatAttachments-тэй ЯГ ижил байх ёстой: ижил зам,
 * ижил мессежийн хэлбэр, ижил хураангуй. Эс бөгөөс апп, вэб хоёр өөр өөр
 * зүйл харна.
 */
const _IMAGE_MAX_WIDTH = 1600;
const _IMAGE_QUALITY = 0.7;

function _two(value) {
  return String(value).padStart(2, "0");
}

/** schools/{id}/chat/{жил}/{өрөө}/{огноо-цаг}_{илгээгч}.{өргөтгөл} */
function imageStoragePath(school, roomId, senderId, fileName) {
  const schoolId = (school || {}).id;
  const year = String(store.state.kSchoolStartYear);
  const base = schoolId ? `schools/${schoolId}/chat/${year}` : "chat";

  const now = new Date();
  const stamp =
    `${now.getFullYear()}${_two(now.getMonth() + 1)}${_two(now.getDate())}-` +
    `${_two(now.getHours())}${_two(now.getMinutes())}${_two(now.getSeconds())}`;

  const dot = String(fileName || "").lastIndexOf(".");
  const ext = dot > -1 ? fileName.slice(dot + 1).toLowerCase() : "";
  return `${base}/${roomId}/${stamp}_${senderId}${ext ? "." + ext : ""}`;
}

/** Өргөнийг нь 1600-аас хэтрүүлэлгүй жижигрүүлж, JPEG болгоно. */
function shrinkImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Зураг уншиж чадсангүй"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Зураг задалж чадсангүй"));
      img.onload = () => {
        const scale = Math.min(1, _IMAGE_MAX_WIDTH / img.width);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error("Хувиргаж чадсангүй"))),
          "image/jpeg",
          _IMAGE_QUALITY,
        );
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

export async function sendImage(school, room, me, file) {
  if (!file) return;

  const blob = await shrinkImage(file);
  const name = String(file.name || "image.jpg").replace(/\.[^.]+$/, "") + ".jpg";
  const path = imageStoragePath(school, room.id, me.id, name);

  // storageRef бол root ref — child() нь аппын FirebaseStorage.ref(path)-тай
  // ижил үнэмлэхүй зам өгнө. uploadToStorage ашиглаж болохгүй: тэр нь
  // `_schoolImages/` угтвар нэмдэг.
  const ref = fb.storageRef.child(path);
  await ref.put(blob, { contentType: "image/jpeg" });
  const url = await ref.getDownloadURL();

  const message = await messagesCol(school, room.id).add({
    chatRoomId: room.id,
    senderId: me.id,
    senderName: me.name,
    content: url,
    type: "image",
    fileName: name,
    fileSize: blob.size,
    storagePath: path,
    timestamp: fb.firestore.FieldValue.serverTimestamp(),
    readBy: { [me.id]: true },
    isEdited: false,
    isDeleted: false,
    delivered: true,
  });

  await roomRef(school, room.id).update({
    lastMessage: "\ud83d\udcf7 Зураг",
    lastMessageTime: fb.firestore.FieldValue.serverTimestamp(),
    lastMessageSender: me.id,
    lastMessageId: message.id,
  });
}

export async function sendPoll(school, room, me, poll) {
  const options = poll.options.filter((text) => String(text).trim() !== "");
  const question = String(poll.question || "").trim();
  if (question === "" || options.length < 2) return;

  const body = {
    question: question,
    options: options,
    anonymous: poll.anonymous !== false,
    multiple: poll.multiple === true,
    closed: false,
    counts: options.map(() => 0),
    voters: 0,
    ...(poll.colors
      ? { bg: String(poll.colors.bg || ""), text: String(poll.colors.text || "") }
      : {}),
  };

  const message = await messagesCol(school, room.id).add({
    chatRoomId: room.id,
    senderId: me.id,
    senderName: me.name,
    content: question,
    type: "poll",
    poll: body,
    timestamp: fb.firestore.FieldValue.serverTimestamp(),
    readBy: { [me.id]: true },
    isEdited: false,
    isDeleted: false,
    delivered: true,
  });

  await roomRef(school, room.id).update({
    lastMessage: "📊 " + question,
    lastMessageTime: fb.firestore.FieldValue.serverTimestamp(),
    lastMessageSender: me.id,
    lastMessageId: message.id,
  });
}

/** Casts or changes a vote. Counts sit on the message, so both move together. */
export async function votePoll(school, roomId, messageId, me, options) {
  const messageRef = messagesCol(school, roomId).doc(messageId);
  const voteRef = messageRef.collection("votes").doc(me.id);

  await fb.db.runTransaction(async (tx) => {
    const messageSnap = await tx.get(messageRef);
    const poll = (messageSnap.data() || {}).poll || {};
    if (poll.closed === true) return;

    const voteSnap = await tx.get(voteRef);
    const before = voteSnap.exists ? voteSnap.data().options || [] : null;

    const counts = [...(poll.counts || [])];
    for (const option of before || []) {
      if (option < counts.length) counts[option] -= 1;
    }
    for (const option of options) {
      if (option < counts.length) counts[option] += 1;
    }

    let voters = poll.voters || 0;
    if (before === null && options.length) voters += 1;
    if (before !== null && !options.length) voters -= 1;

    tx.update(messageRef, {
      "poll.counts": counts,
      "poll.voters": voters < 0 ? 0 : voters,
    });

    if (!options.length) {
      tx.delete(voteRef);
    } else {
      tx.set(voteRef, {
        options: options,
        name: me.name,
        at: fb.firestore.FieldValue.serverTimestamp(),
      });
    }
  });
}

/** What this person picked, so the bubble opens on their own answer. */
export function watchMyVote(school, roomId, messageId, myId, onChange) {
  return messagesCol(school, roomId)
    .doc(messageId)
    .collection("votes")
    .doc(myId)
    .onSnapshot(
      (snap) => onChange(snap.exists ? snap.data().options || [] : []),
      () => onChange([]),
    );
}

/** Who picked what, with each voter's school from the room members. */
export async function pollVotes(school, roomId, messageId) {
  const snap = await messagesCol(school, roomId)
    .doc(messageId)
    .collection("votes")
    .get();

  const votes = [];
  snap.forEach((doc) => {
    const data = doc.data() || {};
    votes.push({
      userId: doc.id,
      name: data.name || "Хэрэглэгч",
      options: data.options || [],
    });
  });
  const members = roomRef(school, roomId).collection("members");
  const schoolDocs = {};
  await Promise.all(
    votes.map(async (vote) => {
      const member = await members.doc(vote.userId).get();
      const data = member.exists ? member.data() : {};
      vote.school = data.school || "";
      if (!vote.school && data.schoolId) {
        if (!schoolDocs[data.schoolId]) {
          schoolDocs[data.schoolId] = fb.db
            .collection("schools")
            .doc(data.schoolId)
            .get();
        }
        const doc = await schoolDocs[data.schoolId];
        vote.school = (doc.exists && doc.data().name) || "";
      }
    }),
  );
  return votes;
}

/**
 * Үндэсний чатын админ мессежийг бэлэн хариултаар тэмдэглэнэ. Сургуулийн
 * тал монгол текстийг нь хардаг; `action` null бол тэмдэг арилна.
 */
export async function setMessageAction(school, roomId, messageId, user, action) {
  await messagesCol(school, roomId)
    .doc(messageId)
    .update({
      action: action
        ? {
            id: action.id,
            mn: action.mn,
            en: action.en,
            at: new Date(),
            by: { id: user.id, name: user.name || "" },
          }
        : null,
    });
}

/** Stops the poll, or starts it again. */
export async function setPollClosed(school, roomId, messageId, closed) {
  await messagesCol(school, roomId).doc(messageId).update({
    "poll.closed": closed,
  });
}

/**
 * Lets the author change the question or the answers. Changing the answers
 * clears the votes — an old vote would point at an answer that moved.
 */
export async function editPoll(
  school,
  roomId,
  messageId,
  question,
  options,
  anonymous,
  colors,
) {
  const messageRef = messagesCol(school, roomId).doc(messageId);
  const snap = await messageRef.get();
  const poll = (snap.data() || {}).poll || {};

  const before = poll.options || [];
  const same =
    before.length === options.length &&
    before.every((text, i) => text === options[i]);

  const settings = {
    "poll.anonymous": anonymous !== false,
    ...(colors
      ? {
          "poll.bg": String(colors.bg || ""),
          "poll.text": String(colors.text || ""),
        }
      : {}),
  };

  if (same) {
    await messageRef.update({
      content: question,
      "poll.question": question,
      ...settings,
    });
    return;
  }

  const votes = await messageRef.collection("votes").get();
  const batch = fb.db.batch();
  votes.forEach((doc) => batch.delete(doc.ref));
  batch.update(messageRef, {
    content: question,
    "poll.question": question,
    "poll.options": options,
    "poll.counts": options.map(() => 0),
    "poll.voters": 0,
    ...settings,
  });
  await batch.commit();
}

/**
 * Нэг хүнд нэг реакц, аппынхтай ижил. `reactions.{userId}` гэсэн цэгтэй
 * замаар бичдэг тул хоёр хүн зэрэг дарахад талбар тус бүр тусад нь нийлж,
 * аль нэг нь алдагдахгүй. Массив байсан бол бүтнээр нь дарж бичих байсан.
 */
export async function setReaction(school, roomId, messageId, myId, emoji) {
  if (!myId) return;
  await messagesCol(school, roomId)
    .doc(messageId)
    .update({
      ["reactions." + myId]: emoji || fb.firestore.FieldValue.delete(),
    });
}

/** Эмодзи бүрээр нь бүлэглэсэн тоо — мессежийн баримт дээрээс шууд. */
export function reactionCounts(message) {
  const counts = {};
  const map = (message || {}).reactions || {};
  for (const emoji of Object.values(map)) {
    counts[emoji] = (counts[emoji] || 0) + 1;
  }
  return counts;
}

/** Soft delete, the same way the mobile app does it. */
export async function deleteMessage(school, roomId, messageId) {
  await messagesCol(school, roomId).doc(messageId).update({
    isDeleted: true,
    deletedAt: fb.firestore.FieldValue.serverTimestamp(),
    content: "",
  });

  // The chat list reads lastMessage from the room, so the deleted text would
  // stay readable there. The mobile app rewrites it as well.
  const newest = await messagesCol(school, roomId)
    .orderBy("timestamp", "desc")
    .limit(1)
    .get();
  if (newest.empty) return;

  const doc = newest.docs[0];
  const last = doc.data() || {};
  await roomRef(school, roomId).update({
    lastMessage:
      last.isDeleted === true ? "Устгагдсан мессеж" : String(last.content || ""),
    lastMessageTime: last.timestamp,
    lastMessageSender: last.senderId,
    lastMessageId: doc.id,
  });
}

export async function editMessage(school, roomId, messageId, content) {
  await messagesCol(school, roomId).doc(messageId).update({
    content: content,
    isEdited: true,
    editedAt: fb.firestore.FieldValue.serverTimestamp(),
  });

  const newest = await messagesCol(school, roomId)
    .orderBy("timestamp", "desc")
    .limit(1)
    .get();
  if (newest.empty || newest.docs[0].id !== messageId) return;
  await roomRef(school, roomId).update({ lastMessage: content });
}

export async function markRead(school, room, messages, myId) {
  const unread = messages.filter(
    (m) => m.senderId !== myId && !(m.readBy || {})[myId],
  );
  if (!unread.length) return;

  const batch = fb.db.batch();
  for (const message of unread.slice(0, 400)) {
    batch.update(messagesCol(school, room.id).doc(message.id), {
      [`readBy.${myId}`]: true,
      [`readAt.${myId}`]: fb.firestore.FieldValue.serverTimestamp(),
    });
  }
  batch.update(roomRef(school, room.id), {
    [`lastReadBy.${myId}`]: unread[unread.length - 1].id,
  });

  await batch.commit();
}

/**
 * Live unread count of one room — the same number the mobile badge shows.
 * Reading is shared, so opening the chat on the phone clears it here too.
 */
export function watchUnreadCount(school, roomId, myId, onChange) {
  return messagesCol(school, roomId)
    .orderBy("timestamp", "desc")
    .limit(50)
    .onSnapshot((snap) => {
      let count = 0;
      snap.forEach((doc) => {
        const data = doc.data() || {};
        if (data.senderId === myId || data.senderId === "system") return;
        if (data.isDeleted === true) return;
        if ((data.readBy || {})[myId] === true) return;
        count++;
      });
      onChange(count);
    },
    // A room the person just left denies the read; count it as nothing new.
    () => onChange(0));
}

/**
 * Whether a room has something new for this person. Read straight from the
 * room document, so the list costs one listener no matter how many rooms.
 */
export function isUnread(room, myId) {
  if (!room.lastMessageId) return false;
  if (room.lastMessageSender === myId) return false;
  return (room.lastReadBy || {})[myId] !== room.lastMessageId;
}

/**
 * Which of the three tabs a room belongs to — the same split the mobile app
 * shows: pupils, parents, and everything else (colleagues).
 */
export function roomTab(room) {
  if (room.userType === "student") return "student";
  if (room.userType === "parent") return "parent";
  return "teacher";
}

/**
 * Аппын хоёр таб: автомат үүссэн бүх өрөө «Албан», гараар үүсгэсэн болон
 * хувийн чат «Хувь». Дүрэм нь chat_service.dart-ийн isOfficialRoom-той ижил.
 */
export function roomScope(room) {
  return isAutoRoom(room) || (room || {}).createdBy === "system"
    ? "official"
    : "private";
}

// ── Starting a chat ─────────────────────────────────────────────────────────

/**
 * The room a one-to-one chat lives in, creating it when it is the first
 * message. `userType` separates a pupil's own chat from their parent's, so it
 * has to match what the mobile app writes: "student", "parent" or "teacher".
 */
export async function openDirectChat(school, me, person) {
  // Two quick clicks used to make two rooms. One job per pair at a time.
  const key = [me.id, person.id].sort().join("_") + "|" + person.userType;
  if (!pendingDirect.has(key)) {
    const job = findOrCreateDirectChat(school, me, person);
    pendingDirect.set(key, job);
    job.catch(() => {}).then(() => pendingDirect.delete(key));
  }
  return pendingDirect.get(key);
}

const pendingDirect = new Map();

async function findOrCreateDirectChat(school, me, person) {
  const existing = await roomsCol(school)
    .where("type", "==", "direct")
    .where("participants", "array-contains", me.id)
    .get();

  const matches = [];
  existing.forEach((doc) => {
    const data = doc.data() || {};
    const participants = data.participants || [];
    if (participants.length !== 2) return;
    if (!participants.includes(person.id)) return;
    if ((data.userType || "") !== person.userType) return;
    matches.push({ ...data, id: doc.id, ref: doc.ref });
  });

  // If the pair somehow has two rooms, open the one the phone opens: the
  // oldest, and by id when even that is equal.
  if (matches.length) {
    const madeAt = (room) =>
      room.createdAt && room.createdAt.toMillis
        ? room.createdAt.toMillis()
        : Infinity;
    matches.sort((a, b) => madeAt(a) - madeAt(b) || (a.id < b.id ? -1 : 1));
    const found = matches[0];
    if (found.deleted === true) {
      await found.ref.update({ deleted: false, deletedAt: null, deletedBy: null });
      found.deleted = false;
    }
    return found;
  }

  const roomId = directRoomId(me.id, person.id);
  const ref = roomRef(school, roomId);
  const room = {
    id: roomId,
    type: "direct",
    name: "Direct Chat",
    participants: [me.id, person.id],
    userType: person.userType,
    createdAt: fb.firestore.FieldValue.serverTimestamp(),
    createdBy: me.id,
    admins: [me.id],
    participantNames: { [me.id]: me.name, [person.id]: person.name },
    deleted: false,
  };

  await fb.db.runTransaction(async (tx) => {
    const doc = await tx.get(ref);
    if (doc.exists && (doc.data() || {}).deleted !== true) return;
    tx.set(ref, room);
  });

  const saved = await ref.get();
  return { ...(saved.data() || room), id: roomId, ref: ref };
}

export async function createGroupChat(school, me, people, name) {
  const participants = [me.id, ...people.map((p) => p.id)];
  const names = { [me.id]: me.name };
  for (const person of people) names[person.id] = person.name;

  // One kind of member keeps the group in that tab on mobile; a mixed group
  // belongs with the staff chats.
  const kinds = [...new Set(people.map((p) => p.userType))];
  const userType = kinds.length === 1 ? kinds[0] : "teacher";

  const room = {
    type: "group",
    name: name,
    participants: participants,
    userType: userType,
    createdAt: fb.firestore.FieldValue.serverTimestamp(),
    createdBy: me.id,
    admins: [me.id],
    participantNames: names,
    deleted: false,
    membersSeeded: true,
  };

  const ref = await roomsCol(school).add(room);

  // Групп бол members-т бичнэ — сургуулийн ч, аймгийн ч ялгаагүй.
  for (const person of [me, ...people]) {
    await ref
      .collection("members")
      .doc(person.id)
      .set(
        {
          id: person.id,
          name: person.name,
          role: person.userType || "",
          roleLabel: roleLabel(person.userType || ""),
          school: school.name || "",
          schoolId: school.ref.id,
          schoolPath: school.ref.path,
          schoolRef: school.ref,
          personId: person.personId || person.id,
          isGlobal: false,
        },
        { merge: true },
      );
  }

  return { ...room, id: ref.id, ref: ref };
}

/** Automatic school and class rooms rebuild themselves, so nobody deletes them. */
export function isAutoRoom(room) {
  const settings = (room || {}).groupSettings || {};
  return (
    settings.isDefaultGroup === true ||
    settings.isStudentClassChat === true ||
    settings.isParentClassChat === true ||
    settings.isParentOnlyChat === true
  );
}

/** A group you made yourself, and you are its admin: yours to delete. */
/// Гараар үүсгэсэн группд зөвхөн админ, үүсгэсэн хүн гишүүн нэмнэ.
export function canAddMembers(room, myId) {
  if (!isManualGroup(room) || !myId) return false;
  return (room.admins || []).includes(myId) || room.createdBy === myId;
}

/// Сонгосон хүмүүсийг группд нэмж, members мөрийг нь бичнэ.
export async function addMembersToGroup(school, room, me, people) {
  const fresh = people.filter(
    (person) => !(room.participants || []).includes(person.id),
  );
  if (!fresh.length) return null;
  if (!canAddMembers(room, me.id)) return "Зөвхөн админ гишүүн нэмнэ";

  const names = { ...(room.participantNames || {}) };
  for (const person of fresh) names[person.id] = person.name;

  await roomRef(school, room.id).update({
    participants: fb.firestore.FieldValue.arrayUnion(
      ...fresh.map((person) => person.id),
    ),
    participantNames: names,
  });
  await seedRoomMembers(
    school,
    room.id,
    fresh.map((person) => person.id),
  );
  await sendSystemMessage(
    school,
    room.id,
    `${me.name} ${fresh.map((p) => p.name).join(', ')}-г группт нэмлээ`,
  );
  return null;
}

export function canDeleteRoom(room, myId) {
  if (!room || room.type !== "group") return false;
  if (isAutoRoom(room)) return false;
  const admins = room.admins || [];
  return admins.includes(myId) || room.createdBy === myId;
}

/** Hides the room from everyone without touching its messages, as mobile does. */
export async function deleteRoom(school, room, myId) {
  if (!canDeleteRoom(room, myId)) return "Зөвхөн админ чатыг устгана";

  await roomRef(school, room.id).update({
    deleted: true,
    deletedAt: fb.firestore.FieldValue.serverTimestamp(),
    deletedBy: myId,
  });
  return null;
}

// ── Автомат группын гишүүн хасах ────────────────────────────────────────────
//
// Аппын lib/chat/services/chat_service.dart-тай ижил дүрэм, ижил талбарууд.
// Автомат группын гишүүдийг sync бүртгэлээс дахин угсардаг тул хасалтыг
// өрөөний `excludedMembers` (энэ чатаас) эсвэл багшийн бичлэгийн
// `excludedFromAllChats` (бүх чатаас) дээр тэмдэглэнэ.

export const SCOPE_ROOM = "room";
export const SCOPE_ALL = "all";

const DEFAULT_EXCLUDE_REASONS = [
  { code: "BLOCK", label: "Блоклох" },
  { code: "FREE", label: "Чөлөөтэй" },
  { code: "LEFT", label: "Ажлаас гарсан" },
  { code: "MATERNITY", label: "Декрэттэй" },
  { code: "OTHER", label: "Бусад" },
];

export const REASON_COLORS = {
  BLOCK: "#F43F5E",
  FREE: "#EAB308",
  LEFT: "#A855F7",
  MATERNITY: "#EC4899",
  OTHER: "#4F7FF0",
};

export function reasonColor(code, index) {
  const palette = Object.values(REASON_COLORS).concat("#10B981");
  return REASON_COLORS[code] || palette[index % palette.length];
}

const REASON_ICONS = {
  BLOCK: "mdi-cancel",
  FREE: "mdi-clock-outline",
  LEFT: "mdi-account-group",
  MATERNITY: "mdi-file-document-outline",
  OTHER: "mdi-check-circle-outline",
};

export function reasonIcon(code, index) {
  const icons = Object.values(REASON_ICONS).concat("mdi-circle-outline");
  return REASON_ICONS[code] || icons[index % icons.length];
}

/** Шалтгааны жагсаалт. Тохиргооны цуглуулга хоосон бол суурь жагсаалт. */
export async function excludeReasons() {
  try {
    const snap = await fb.db.collection("_auto_chat_exclude_reasons").get();
    const rows = [];
    snap.forEach((doc) => {
      const data = doc.data() || {};
      const label = String(data.label || data.name || "").trim();
      if (!label) return;
      rows.push({
        code: String(data.code || doc.id),
        label: label,
        order: Number(data.order ?? 99),
      });
    });
    if (rows.length) return rows.sort((a, b) => a.order - b.order);
  } catch (e) {
    console.warn("excludeReasons", e);
  }
  return DEFAULT_EXCLUDE_REASONS;
}

/** Зөвхөн сургуулийн автомат группт — ангийн чат, мэргэжилтний группт биш. */
export function canManageAutoMembers(room) {
  const settings = (room || {}).groupSettings || {};
  if (settings.isDefaultGroup !== true) return false;
  if (String(room.id || "").startsWith("grp_city")) return false;
  if (room.userType === "citymanager") return false;
  return true;
}

/** Багш админ зөвхөн өөрийн чатаас хасна; менежер, захирал бүх чатаас хасах,
 *  бүх чатаас хассаныг буцаах эрхтэй. */
export function canExcludeGlobally(userData) {
  const role = String((userData && userData.role) || "").toLowerCase();
  return role === "schoolmanager" || role === "schooldirector";
}

export function isLeaderRole(role) {
  const r = String(role || "").toLowerCase();
  return r === "schoolmanager" || r === "schooldirector";
}

/** Гарсан хүн админаар үлдэхгүй; members дэд цуглуулгаас ч арилна. */
export async function removeFromGroup(school, roomId, userId) {
  const room = roomRef(school, roomId);
  await room.update({
    participants: fb.firestore.FieldValue.arrayRemove(userId),
    admins: fb.firestore.FieldValue.arrayRemove(userId),
  });
  await room.collection("members").doc(userId).delete();
}

async function addToGroup(school, roomId, userId) {
  const teacher = await school.ref.collection("teachers").doc(userId).get();
  const data = teacher.data() || {};
  const role = String(data.role || "").toLowerCase();
  const room = roomRef(school, roomId);
  await room.update({
    participants: fb.firestore.FieldValue.arrayUnion(userId),
  });
  await room
    .collection("members")
    .doc(userId)
    .set(staffMemberDoc(school, teacher, data, role, memberName(data)), {
      merge: true,
    });
}

async function settlePendingRequest(school, roomId, targetId, myId) {
  const ref = roomRef(school, roomId)
    .collection("removalRequests")
    .doc(targetId);
  const doc = await ref.get();
  if (!doc.exists || (doc.data() || {}).status !== "pending") return;
  await ref.update({
    status: "approved",
    answeredBy: myId,
    answeredAt: fb.firestore.FieldValue.serverTimestamp(),
  });
}

/** Бүх чатаас хассан тэмдэглэл багшийн бичлэг дээр: `excludes`-д код,
 *  `excludesDetail`-д дэлгэрэнгүй, `excludedFromAllChats`-д sync-ийн туг. */
async function writeExcludeRecord(school, targetId, reason, myId, add) {
  const ref = school.ref.collection("teachers").doc(targetId);
  const data = (await ref.get()).data() || {};
  const detail = (data.excludesDetail || []).filter((r) => r.roomId !== "ALL");
  if (add) {
    detail.push({
      roomId: "ALL",
      reasonCode: reason.code,
      reasonLabel: reason.label,
      excludedAt: fb.firestore.Timestamp.now(),
      excludedBy: myId,
    });
  }
  const del = fb.firestore.FieldValue.delete();
  await ref.set(
    {
      excludes: add ? [reason.code] : del,
      excludesDetail: detail.length ? detail : del,
      excludedFromAllChats: add ? true : del,
      excludedAt: add ? fb.firestore.Timestamp.now() : del,
      excludedBy: add ? myId : del,
    },
    { merge: true },
  );
}

/** Сургуулийн бүх автомат группээс тэр даруй хасна. Ангийн чат хамаарахгүй. */
async function removeFromAllAutoRooms(school, targetId, myId) {
  const snap = await roomsCol(school)
    .where("participants", "array-contains", targetId)
    .get();
  for (const doc of snap.docs) {
    const settings = (doc.data() || {}).groupSettings || {};
    if (settings.isDefaultGroup !== true) continue;
    await removeFromGroup(school, doc.id, targetId);
    await settlePendingRequest(school, doc.id, targetId, myId);
  }
}

/** Админ шууд шалтгаантай хасна. scope: SCOPE_ROOM | SCOPE_ALL. */
export async function excludeMember(school, room, userData, targetId, reason, scope) {
  const myId = String(userData.id);
  const all = scope === SCOPE_ALL && canExcludeGlobally(userData);
  try {
    if (all) {
      await writeExcludeRecord(school, targetId, reason, myId, true);
      await removeFromAllAutoRooms(school, targetId, myId);
    } else {
      await roomRef(school, room.id)
        .update({
          excludedMembers: fb.firestore.FieldValue.arrayUnion(targetId),
          [`excludedInfo.${targetId}`]: {
            reasonCode: reason.code,
            reasonLabel: reason.label,
            excludedAt: fb.firestore.FieldValue.serverTimestamp(),
            excludedBy: myId,
          },
        });
      await removeFromGroup(school, room.id, targetId);
    }
    await settlePendingRequest(school, room.id, targetId, myId);
    return null;
  } catch (e) {
    console.warn("excludeMember", e);
    return "Хасаж чадсангүй";
  }
}

/** Хасагдсан хүмүүс — энэ чатаас хассан нь ч, бүх чатаас хассан нь ч. */
export async function excludedMembers(school, year, room) {
  const rows = [];
  const fresh = (await roomRef(school, room.id).get()).data() || {};
  const ids = fresh.excludedMembers || [];
  const info = fresh.excludedInfo || {};
  const names = await loadPeopleInfo(school, year, ids);
  for (const id of ids) {
    const one = info[id] || {};
    rows.push({
      id: id,
      scope: SCOPE_ROOM,
      name: (names[id] || {}).name || "Тодорхойгүй",
      label: one.reasonLabel || "",
      code: one.reasonCode || "",
    });
  }

  const snap = await school.ref
    .collection("teachers")
    .where("excludedFromAllChats", "==", true)
    .get();
  snap.forEach((doc) => {
    const data = doc.data() || {};
    const detail = (data.excludesDetail || []).find((r) => r.roomId === "ALL") || {};
    rows.push({
      id: doc.id,
      scope: SCOPE_ALL,
      name: staffName(data) || "Тодорхойгүй",
      label: detail.reasonLabel || "",
      code: detail.reasonCode || "",
    });
  });
  return rows;
}

/** Тэмдэглэгээг арилгаад гишүүнийг эргүүлж оруулна. */
export async function restoreMember(school, room, userData, row) {
  const all = row.scope === SCOPE_ALL;
  if (all && !canExcludeGlobally(userData)) {
    return "Зөвхөн менежер, захирал буцаана";
  }
  const myId = String(userData.id);
  try {
    if (all) {
      await writeExcludeRecord(school, row.id, { code: row.code }, myId, false);
    } else {
      await roomRef(school, room.id)
        .update({
          excludedMembers: fb.firestore.FieldValue.arrayRemove(row.id),
          [`excludedInfo.${row.id}`]: fb.firestore.FieldValue.delete(),
        });
    }
    await addToGroup(school, room.id, row.id);
    // Бүх чатаас буцаасан хүнийг бусад бүлэгт нь sync л оруулна.
    if (all) await resyncSchoolGroups(school, myId);
    return null;
  } catch (e) {
    console.warn("restoreMember", e);
    return "Буцааж чадсангүй";
  }
}

/** Нэг хүнд нэг хүлээгдэж буй хүсэлт — баримтын id нь хасах хүний id. */
export async function requestMemberRemoval(school, room, userData, targetId, reason) {
  try {
    await roomRef(school, room.id)
      .collection("removalRequests")
      .doc(targetId)
      .set({
        targetId: targetId,
        reasonCode: reason.code,
        reasonLabel: reason.label,
        status: "pending",
        requestedBy: String(userData.id),
        requestedByName: String(userData.FIRST_NAME || userData.firstName || ""),
        requestedAt: fb.firestore.FieldValue.serverTimestamp(),
      });
    return null;
  } catch (e) {
    console.warn("requestMemberRemoval", e);
    return "Хүсэлт илгээж чадсангүй";
  }
}

export async function pendingRemovalRequests(school, roomId) {
  try {
    const snap = await roomRef(school, roomId)
      .collection("removalRequests")
      .where("status", "==", "pending")
      .get();
    return snap.docs.map((d) => ({ ...d.data(), id: d.id }));
  } catch (e) {
    console.warn("pendingRemovalRequests", e);
    return [];
  }
}

/** Зөвшөөрвөл шууд хасахтай ижил замаар төлөв тавигдаж хасагдана. */
export async function resolveRemovalRequest(school, room, userData, request, approve, scope) {
  const targetId = String(request.targetId || request.id);
  try {
    await roomRef(school, room.id)
      .collection("removalRequests")
      .doc(targetId)
      .update({
        status: approve ? "approved" : "rejected",
        answeredBy: String(userData.id),
        answeredAt: fb.firestore.FieldValue.serverTimestamp(),
      });
    if (!approve) return null;
    return excludeMember(
      school,
      room,
      userData,
      targetId,
      { code: request.reasonCode || "", label: request.reasonLabel || "" },
      scope || SCOPE_ROOM,
    );
  } catch (e) {
    console.warn("resolveRemovalRequest", e);
    return "Хүсэлтийг шийдэж чадсангүй";
  }
}

/** Session-ий тэмдгийг арилгаад бүх автомат өрөөг дахин угсарна. */
export async function resyncSchoolGroups(school, myId) {
  try {
    window.sessionStorage.removeItem(`chatGroups:2:${school.id || school.ref.id}`);
  } catch (e) {
    // storage байхгүй бол ensureSchoolGroups өөрөө ажиллана.
  }
  await ensureSchoolGroups(school, myId);
}

// ── Ангийн чат ─────────────────────────────────────────────────────────────
//
// Аппын lib/chat/services/chat_service.dart-ын ангийн чаттай ижил: ижил
// query, ижил талбар, ижил system message. Багш өөрийн удирддаг ангиудын
// сурагчийн болон эцэг эхийн чатыг, сурагч өөрийн ангийн чатыг үүсгэж,
// нээх бүрд гишүүдийг бүртгэлтэй нь тааруулна. Эцэг эх вэбэд нэвтэрдэггүй
// тул эцэг эхийн хоорондын чат энд үүсэхгүй, гишүүд нь л sync хийгдэнэ.

function yearStr() {
  return String(store.state.kSchoolStartYear);
}

/** int.tryParse(id) ?? id — бүртгэлд STUDENT_GROUP_ID тоогоор хадгалагддаг. */
function groupIdValue(id) {
  const n = Number(id);
  return Number.isFinite(n) && String(n) === String(id) ? n : id;
}

function isDeletedRoom(doc) {
  return (doc.data() || {}).deleted === true;
}

function isParentOnlyRoom(doc) {
  const settings = (doc.data() || {}).groupSettings || {};
  return settings.isParentOnlyChat === true;
}

const teacherChatIdCache = {};

/** Багшийн ямар ч id-г (doc id, INSTRUCTOR_ID, PERSON_ID) чатын id болгоно. */
async function teacherChatIdFromAnyId(school, rawId) {
  if (!rawId) return null;
  if (teacherChatIdCache[rawId]) return teacherChatIdCache[rawId];
  const teachers = school.ref.collection("teachers");
  const direct = await teachers.doc(rawId).get();
  if (direct.exists && !isGone(direct.data())) {
    teacherChatIdCache[rawId] = rawId;
    return rawId;
  }
  const asInt = Number(rawId);
  const values = Number.isFinite(asInt) ? [asInt, rawId] : [rawId];
  for (const field of ["INSTRUCTOR_ID", "PERSON_ID"]) {
    for (const value of values) {
      const query = await teachers.where(field, "==", value).get();
      const live = query.docs.find((doc) => !isGone(doc.data()));
      if (live) {
        teacherChatIdCache[rawId] = live.id;
        return live.id;
      }
    }
  }
  return null;
}

/** Ангийн даргалагч багшийн чатын id. */
async function resolveClassTeacherChatId(school, groupId) {
  const doc = await school.ref
    .collection(`classGroups-${yearStr()}`)
    .doc(groupId)
    .get();
  if (!doc.exists) return null;
  const data = doc.data() || {};
  const raw = data.TEACHER_ID ?? data.INSTRUCTOR_ID;
  if (raw === undefined || raw === null || String(raw) === "") return null;
  return teacherChatIdFromAnyId(school, String(raw));
}

/** Ангийн одоогийн гишүүд: багш, дараа нь сурагчид эсвэл parent_ id-ууд. */
async function currentClassParticipants(school, groupId, userType, includeTeacher = true) {
  const students = await school.ref
    .collection(`students-${yearStr()}`)
    .where("STUDENT_GROUP_ID", "==", groupIdValue(groupId))
    .get();
  if (students.empty) return [];

  const participants = [];
  const teacherId = includeTeacher ? await resolveClassTeacherChatId(school, groupId) : null;
  if (teacherId) participants.push(teacherId);

  students.forEach((doc) => {
    const data = doc.data() || {};
    if (data.deleted === true || data.moved === true) return;
    const id = userType === "parent" ? `parent_${doc.id}` : doc.id;
    if (!participants.includes(id)) participants.push(id);
  });
  return participants;
}

async function sendSystemMessage(school, roomId, content) {
  const room = roomRef(school, roomId);
  await room.collection("messages").add({
    content: content,
    senderId: "system",
    senderName: "System",
    timestamp: fb.firestore.FieldValue.serverTimestamp(),
    type: "system",
    isDeleted: false,
    isEdited: false,
    targetUserId: null,
  });
  await room.update({
    lastMessage: content,
    lastMessageTime: fb.firestore.FieldValue.serverTimestamp(),
    lastMessageSender: "system",
  });
}

/** Ангийн чатын гишүүний баримт — аппын seedRoomMembers-тэй ижил. */
async function classMemberDoc(school, id) {
  const base = {
    school: school.name || "",
    schoolId: school.ref.id,
    schoolPath: school.ref.path,
    schoolRef: school.ref,
    isGlobal: false,
  };
  const students = school.ref.collection(`students-${yearStr()}`);

  if (id.startsWith("parent_")) {
    const student = await students.doc(id.substring(7)).get();
    if (!student.exists) return null;
    return {
      id: id,
      name: `Эцэг эх (${studentName(student.data() || {})})`,
      role: "parent",
      roleLabel: "Эцэг эх",
      ...base,
    };
  }

  const teacher = await school.ref.collection("teachers").doc(id).get();
  if (teacher.exists) {
    const data = teacher.data() || {};
    const role = String(data.role || "").toLowerCase();
    return {
      id: id,
      name: staffName(data),
      role: role,
      roleLabel: roleLabel(role),
      ...base,
    };
  }

  const student = await students.doc(id).get();
  if (!student.exists) return null;
  const data = student.data() || {};
  const className = String(data.STUDENT_GROUP_NAME || data.classGroupName || "").trim();
  return {
    id: id,
    name: studentName(data),
    role: "student",
    roleLabel: className ? `Сурагч · ${className}` : "",
    ...base,
  };
}

async function seedRoomMembers(school, roomId, ids) {
  const members = roomRef(school, roomId).collection("members");
  let batch = fb.db.batch();
  let pending = 0;
  for (const id of ids) {
    if (!id) continue;
    const doc = await classMemberDoc(school, id);
    if (!doc) continue;
    batch.set(members.doc(id), doc, { merge: true });
    if (++pending === 400) {
      await batch.commit();
      batch = fb.db.batch();
      pending = 0;
    }
  }
  if (pending > 0) await batch.commit();
}

async function removeRoomMembers(school, roomId, ids) {
  const members = roomRef(school, roomId).collection("members");
  let batch = fb.db.batch();
  let pending = 0;
  for (const id of ids) {
    if (!id) continue;
    batch.delete(members.doc(id));
    if (++pending === 400) {
      await batch.commit();
      batch = fb.db.batch();
      pending = 0;
    }
  }
  if (pending > 0) await batch.commit();
}

async function ensureRoomName(school, room, suffix) {
  const name = String(room.name || "");
  const prefix = name.includes(" - ") ? name.split(" - ")[0].trim() : "";
  const desired = prefix ? `${prefix} - ${suffix}` : suffix;
  if (name === desired) return;
  await roomRef(school, room.id).update({ name: desired });
}

/** Гишүүдийг бүртгэлтэй тааруулна; даргалагч багш үргэлж админ. */
async function updateChatRoomParticipants(school, room, groupId, userType, includeTeacher = true) {
  const required = await currentClassParticipants(school, groupId, userType, includeTeacher);
  if (!required.length) return null;

  const teacherId = includeTeacher ? await resolveClassTeacherChatId(school, groupId) : null;
  const admins = [...(room.admins || [])];
  if (teacherId && !admins.includes(teacherId)) admins.push(teacherId);
  const adminsChanged = admins.length !== (room.admins || []).length;

  // Teacher unresolved: keep the room's admins instead of stripping them.
  if (includeTeacher && !teacherId) {
    for (const id of room.admins || []) {
      if (!required.includes(id)) required.push(id);
    }
  }
  const existing = room.participants || [];
  const missing = required.filter((id) => !existing.includes(id));
  const extra = existing.filter((id) => !required.includes(id));

  if (!missing.length && !extra.length && !adminsChanged) return room;

  const update = { participants: required };
  if (adminsChanged) update.admins = admins;
  await roomRef(school, room.id).update(update);
  await seedRoomMembers(school, room.id, missing);
  await removeRoomMembers(school, room.id, extra);

  if (missing.length) {
    await sendSystemMessage(
      school,
      room.id,
      userType === "parent"
        ? `Шинэ эцэг эх нар группд нэгдлээ (${missing.length})`
        : `Шинэ сурагчид группд нэгдлээ (${missing.length})`,
    );
  }

  return { ...room, participants: required, admins: admins };
}

export async function syncRoomParticipants(school, room) {
  if (!room) return null;
  if (!room.STUDENT_GROUP_ID) return dropGoneParticipants(school, room);
  if (!room.userType) return null;
  if (room.lessonYear && Number(room.lessonYear) !== Number(store.state.kSchoolStartYear)) {
    return null;
  }
  const doc = await roomRef(school, room.id).get();
  if (!doc.exists || isDeletedRoom(doc)) return null;
  return updateChatRoomParticipants(
    school,
    { ...doc.data(), id: doc.id },
    room.STUDENT_GROUP_ID,
    room.userType,
    !isParentOnlyRoom(doc),
  );
}

export function isManualGroup(room) {
  return (
    !!room &&
    room.type === "group" &&
    !room.STUDENT_GROUP_ID &&
    roomScope(room) === "private" &&
    !String(room.id || "").startsWith("grp_city") &&
    room.userType !== "citymanager"
  );
}

export async function dropGoneParticipants(school, room) {
  if (!isManualGroup(room)) return null;
  const ids = room.participants || [];
  const gone = await goneParticipantIds(school, ids);
  if (!gone.length) return null;
  await roomRef(school, room.id)
    .update({ participants: fb.firestore.FieldValue.arrayRemove(...gone) });
  await removeRoomMembers(school, room.id, gone);
  return { ...room, participants: ids.filter((id) => !gone.includes(id)) };
}

async function goneParticipantIds(school, ids) {
  const gone = [];
  const plain = ids.filter((id) => !id.startsWith("parent_"));
  const staff = await docsByIds(school.ref.collection("teachers"), plain);
  const foundStaff = new Set();
  staff.forEach((doc) => {
    foundStaff.add(doc.id);
    if (isGone(doc.data())) gone.push(doc.id);
  });
  const studentIds = [
    ...new Set([
      ...plain.filter((id) => !foundStaff.has(id)),
      ...ids.filter((id) => id.startsWith("parent_")).map((id) => id.slice(7)),
    ]),
  ];
  const students = await docsByIds(
    school.ref.collection(`students-${yearStr()}`),
    studentIds,
  );
  students.forEach((doc) => {
    if (!isGone(doc.data())) return;
    if (ids.includes(doc.id)) gone.push(doc.id);
    if (ids.includes(`parent_${doc.id}`)) gone.push(`parent_${doc.id}`);
  });
  return gone;
}

function isGone(data) {
  const d = data || {};
  return d.deleted === true || d.moved === true;
}

async function docsByIds(col, ids) {
  const docs = [];
  for (let i = 0; i < ids.length; i += 10) {
    let snap = null;
    try {
      snap = await col
        .where(fb.firestore.FieldPath.documentId(), "in", ids.slice(i, i + 10))
        .get();
    } catch (e) {
      continue;
    }
    snap.forEach((doc) => docs.push(doc));
  }
  return docs;
}

async function classGroupName(school, groupId) {
  const snap = await school.ref
    .collection(`students-${yearStr()}`)
    .where("STUDENT_GROUP_ID", "==", groupIdValue(groupId))
    .limit(1)
    .get();
  if (snap.empty) return null;
  return String((snap.docs[0].data() || {}).STUDENT_GROUP_NAME || "Анги");
}

export function directRoomId(a, b) {
  const pair = [String(a), String(b)].sort();
  return `direct_${pair[0]}_${pair[1]}`;
}

// The year whose classGroups holds the group: this year, else last year, else
// none — same rule as the app, so a room for last year's class never gets this year.
async function lessonYearOfGroup(school, groupId) {
  const now = Number(store.state.kSchoolStartYear);
  for (const year of [now, now - 1]) {
    const doc = await school.ref.collection("classGroups-" + year).doc(String(groupId)).get();
    if (doc.exists) return year;
  }
  return null;
}

async function createClassRoom(school, myId, groupId, participants, fields) {
  const teacherId = fields.includeTeacher
    ? await resolveClassTeacherChatId(school, groupId)
    : null;
  const admins = fields.includeTeacher
    ? [...new Set([teacherId, myId].filter(Boolean))]
    : [];

  // Тогтмол id + transaction: апптай зэрэг үүсгэсэн ч нэг л өрөө.
  const roomId = `class_${groupId}_${fields.kind}`;
  const ref = roomRef(school, roomId);
  const lessonYear = await lessonYearOfGroup(school, groupId);
  const created = await fb.db.runTransaction(async (tx) => {
    const doc = await tx.get(ref);
    if (doc.exists && (doc.data() || {}).deleted !== true) return false;
    tx.set(ref, {
      id: roomId,
      type: "group",
      name: fields.name,
      description: fields.description,
      participants: participants,
      admins: admins,
      createdAt: fb.firestore.FieldValue.serverTimestamp(),
      createdBy: myId,
      lastMessage: null,
      lastMessageTime: null,
      lastMessageSender: null,
      STUDENT_GROUP_ID: groupId,
      ...(lessonYear ? { lessonYear } : {}),
      deleted: false,
      membersSeeded: true,
      userType: fields.userType,
      groupSettings: {
        ...fields.settings,
        canMembersAddOthers: false,
        canMembersEditInfo: false,
      },
    });
    return true;
  });

  if (!created) {
    const doc = await ref.get();
    return { ...doc.data(), id: doc.id };
  }

  seedRoomMembers(school, ref.id, participants).catch((e) =>
    console.warn("seedRoomMembers", e),
  );
  await sendSystemMessage(school, ref.id, fields.welcome);
  return { id: ref.id, participants: participants, admins: admins };
}

// Нэг ангийн чатыг зэрэг хоёр удаа үүсгэхгүй — аппын _pendingClassChats-тай
// ижил: явж байгаа үүсгэлт байвал түүнийгээ хүлээнэ.
const pendingClassChats = {};

function once(key, make) {
  if (pendingClassChats[key]) return pendingClassChats[key];
  const job = make().finally(() => delete pendingClassChats[key]);
  pendingClassChats[key] = job;
  return job;
}

// Newcomers are not in the room yet, so the on-open sync can't reach them.
async function joinIfMissing(school, myId, room, groupId, userType) {
  if ((room.participants || []).includes(myId)) return room;
  return (await updateChatRoomParticipants(school, room, groupId, userType)) || room;
}

/** Ангийн сурагчид болон багшийн чат. */
function getOrCreateStudentClassChat(school, myId, groupId) {
  if (!groupId) return Promise.resolve(null);
  return once(`student_${groupId}`, () =>
    findOrCreateStudentClassChat(school, myId, groupId),
  );
}

async function findOrCreateStudentClassChat(school, myId, groupId) {
  const existing = await roomsCol(school)
    .where("type", "==", "group")
    .where("STUDENT_GROUP_ID", "==", groupId)
    .where("userType", "==", "student")
    .get();
  const live = existing.docs.filter((doc) => !isDeletedRoom(doc));
  if (live.length) {
    const room = { ...live[0].data(), id: live[0].id };
    return joinIfMissing(school, myId, room, groupId, "student");
  }

  const participants = await currentClassParticipants(school, groupId, "student");
  if (!participants.length) return null;
  const groupName = await classGroupName(school, groupId);
  if (!groupName) return null;

  return createClassRoom(school, myId, groupId, participants, {
    includeTeacher: true,
    kind: "student",
    name: `${groupName} - Ангийн чат`,
    description: "Ангийн сурагчид болон багшийн group чат",
    userType: "student",
    settings: { isStudentClassChat: true },
    welcome:
      "Ангийн group чатад тавтай морил! Та багш болон ангийнхантайгаа ярилцах боломжтой.",
  });
}

/** Ангийн эцэг эх нар болон багшийн чат. */
function getOrCreateParentClassChat(school, myId, groupId) {
  if (!groupId) return Promise.resolve(null);
  return once(`parent_${groupId}`, () =>
    findOrCreateParentClassChat(school, myId, groupId),
  );
}

async function findOrCreateParentClassChat(school, myId, groupId) {
  const existing = await roomsCol(school)
    .where("type", "==", "group")
    .where("STUDENT_GROUP_ID", "==", groupId)
    .where("userType", "==", "parent")
    .get();
  // Ангид эцэг эхийн хоорондын өрөө ч ижил түлхүүртэй — түүнийг алгасна.
  const withTeacher = existing.docs.filter(
    (doc) => !isParentOnlyRoom(doc) && !isDeletedRoom(doc),
  );
  if (withTeacher.length) {
    const room = { ...withTeacher[0].data(), id: withTeacher[0].id };
    await ensureRoomName(school, room, "Багш, эцэг эхийн чат");
    return joinIfMissing(school, myId, room, groupId, "parent");
  }

  const participants = await currentClassParticipants(school, groupId, "parent");
  if (!participants.length) return null;
  const groupName = await classGroupName(school, groupId);
  if (!groupName) return null;

  return createClassRoom(school, myId, groupId, participants, {
    includeTeacher: true,
    kind: "parent",
    name: `${groupName} - Багш, эцэг эхийн чат`,
    description: "Ангийн эцэг эх нар, багшийн group чат",
    userType: "parent",
    settings: { isParentClassChat: true },
    welcome:
      "Эцэг эх нарын group чатад тавтай морил! Та хүүхдийнхээ ангийн асуудлаар ярилцах боломжтой.",
  });
}

/** Багшийн удирддаг ангиуд — classGroups.TEACHER_ID нь INSTRUCTOR_ID. */
async function supervisedClassGroupIds(school, userData) {
  const myId = String(userData.id);
  const candidates = new Set([myId]);
  if (userData.PERSON_ID) candidates.add(String(userData.PERSON_ID));
  const teacher = await school.ref.collection("teachers").doc(myId).get();
  const instructorId = (teacher.data() || {}).INSTRUCTOR_ID;
  if (instructorId !== undefined && instructorId !== null) {
    candidates.add(String(instructorId));
  }

  const groups = school.ref.collection(`classGroups-${yearStr()}`);
  const ids = new Set();
  for (const raw of candidates) {
    const asInt = Number(raw);
    const values = Number.isFinite(asInt) ? [asInt, raw] : [raw];
    for (const value of values) {
      const snap = await groups.where("TEACHER_ID", "==", value).get();
      snap.forEach((doc) => ids.add(doc.id));
    }
  }
  return [...ids];
}

/** Багш чат нээхэд — аппын autoCreateTeacherStudentChats +
 *  autoCreateTeacherParentChats. */
export async function ensureTeacherClassChats(school, userData) {
  const myId = String(userData.id);
  const groupIds = await supervisedClassGroupIds(school, userData);
  for (const groupId of groupIds) {
    try {
      await getOrCreateStudentClassChat(school, myId, groupId);
    } catch (e) {
      console.warn("student class chat", groupId, e);
    }
  }
  for (const groupId of groupIds) {
    try {
      await getOrCreateParentClassChat(school, myId, groupId);
    } catch (e) {
      console.warn("parent class chat", groupId, e);
    }
  }
}

/** The phone's getMyClassGroupChat: find the pupil's class room, else create it. */
export async function ensureStudentClassChat(school, userData) {
  const myId = String(userData.id);
  const student = await school.ref.collection(`students-${yearStr()}`).doc(myId).get();
  if (!student.exists) return null;
  const groupId = (student.data() || {}).STUDENT_GROUP_ID;
  if (groupId === undefined || groupId === null) return null;
  const groupIdStr = String(groupId);

  const groupDoc = await school.ref
    .collection(`classGroups-${yearStr()}`)
    .doc(groupIdStr)
    .get();
  if (!groupDoc.exists) return null;
  const teacherId = (groupDoc.data() || {}).TEACHER_ID;
  if (teacherId === undefined || teacherId === null) return null;

  const found = await roomsCol(school)
    .where("type", "==", "group")
    .where("participants", "array-contains", myId)
    .where("groupSettings.isStudentClassChat", "==", true)
    .where("groupSettings.classTeacherId", "==", String(teacherId))
    .get();
  if (!found.empty) return { ...found.docs[0].data(), id: found.docs[0].id };

  return getOrCreateStudentClassChat(school, myId, groupIdStr);
}

// ── Rooms the school always has ─────────────────────────────────────────────
//
// The mobile app builds the same rooms with the same ids
// (lib/pages_v2/chat_directory/school_group_chats.dart). Whichever app a
// colleague opens first creates them; the other one just finds them.

export async function ensureSchoolGroups(school, myId) {
  // Every page load used to re-read the whole staff list; once per browser
  // session is enough, the mobile app keeps them in step as well. The version
  // in the key makes a tab that stamped itself under the old sync run the new
  // one once.
  const stamp = `chatGroups:2:${school.id || school.ref.id}`;
  try {
    if (window.sessionStorage.getItem(stamp)) return;
  } catch (e) {
    // Private mode without storage — fall through and just do the work.
  }

  // Тэмдгийг ажил АМЖИЛТТАЙ дууссаны дараа тавина. Урьдчилж тавибал
  // дундуур алдаа гарахад тухайн session-д дахин оролдохгүй болно.
  const markDone = () => {
    try {
      window.sessionStorage.setItem(stamp, "1");
    } catch (e) {
      // storage байхгүй — дараагийн ачаалалтад дахин ажиллана.
    }
  };

  const snap = await school.ref.collection("teachers").get();

  const staff = [];
  const teachers = [];
  const leaders = [];
  const managers = [];
  const byType = new Map();
  const byDepartment = new Map();

  const people = {};
  const rows = {};
  const demoIds = new Set();

  snap.forEach((doc) => {
    const data = doc.data() || {};
    if (data.deleted === true || data.moved === true) return;
    // Апп-аас бүх чатаас хассан хүн устсантай адил — эргэж нэмэгдэхгүй.
    if (data.excludedFromAllChats === true) return;

    const role = String(data.role || "").toLowerCase();
    // USERNAME-гүй багшийн бичлэг жинхэнэ бүртгэл биш. Захирал, менежерт
    // USERNAME байдаггүй тул тэднийг шалгахгүй.
    if (role === "teacher" && !String(data.USERNAME || "").trim()) return;
    const name = memberName(data);
    if (name === "-" && role === "") return;

    people[doc.id] = staffMemberDoc(school, doc, data, role, name);

    rows[doc.id] = data;
    staff.push(doc.id);
    // Демо бүртгэл гишүүн болно, гэхдээ админд нэр дэвшихгүй — апп түүнийг
    // admins-аас хасдаг тул энд нэмбэл хоёр систем бие биеэ дарна.
    const isDemo = data.isDemo === true;
    if (isDemo) demoIds.add(doc.id);
    if (!isDemo && (role === "schooldirector" || role === "schoolmanager")) {
      leaders.push(doc.id);
    }
    if (!isDemo && role === "schoolmanager") managers.push(doc.id);
    if (role === "teacher") teachers.push(doc.id);

    // Багшийн төрөл: сургалтын менежер ч хамаарна. Нэр нь хүн бүрд
    // бичигдээгүй байдаг тул бүлгийн хэн нэгнээс олдвол бүгдэд хамаарна.
    if (role === "teacher" || role === "schoolmanager") {
      const typeId = String(data.INSTRUCTOR_TYPE_ID ?? "").trim();
      if (typeId) {
        const raw = String(data.INSTRUCTOR_TYPE_NAME ?? "").trim();
        const typeName = raw.endsWith("багш") ? `${raw} нар` : raw;
        if (!byType.has(typeId)) byType.set(typeId, { name: "", members: [] });
        const group = byType.get(typeId);
        if (!group.name && typeName) group.name = typeName;
        group.members.push(doc.id);
      }
    }

    // Заах аргын нэгдэлд бүртгэлдээ тэр нэгдлийг заасан менежер ч орно.
    if (role === "teacher" || role === "schoolmanager") {
      const deptId = String(data.SUBJECT_DEPARTMENT_ID ?? "").trim();
      const deptName = String(data.SUBJECT_DEPARTMENT_NAME ?? "").trim();
      if (deptId && deptName) {
        if (!byDepartment.has(deptId)) {
          byDepartment.set(deptId, { name: deptName, members: [] });
        }
        byDepartment.get(deptId).members.push(doc.id);
      }
    }
  });

  if (!staff.length) return;
  if (!staff.includes(myId)) return;

  // Өрөө бүрийг нэг нэгээр уншихад мөр тутамд дуудалт болж, бичилт бүр
  // тусдаа stream event үүсгэдэг байсан. Нэг уншилт, нэг batch.
  const roomsSnap = await roomsCol(school)
    .where("groupSettings.isDefaultGroup", "==", true)
    .get();
  const existing = {};
  roomsSnap.forEach((doc) => {
    existing[doc.id] = doc.data() || {};
  });
  const writer = makeWriter(school);
  const base = { school, people, existing, writer, myId, demoIds };

  // Тохиргоо байвал түүгээр, хоосон бол доорх суурь өрөөнүүдээр. Уншиж
  // чадаагүй бол юу ч хийхгүй: буруу жагсаалтаар бичихээс огт бичихгүй нь дээр.
  const config = await loadRoomConfig();
  if (config === null) return;

  const live = new Set();
  const prefixes = new Set();

  if (config.length) {
    for (const room of config) {
      if (!room.filterColumn.length) {
        const members = idsForRoles(rows, room.roles);
        if (!members.length) continue;
        // Гишүүд сонгоход хоосон role жагсаалт нь "бүгд" гэсэн үг, харин
        // админд "хэнийг ч заагаагүй" гэсэн үг. Урвуугаар уншсанаас болж
        // Бүх багш нар чатад бүх багш админ болсон.
        const admins = room.adminRoles.length
          ? idsForRoles(rows, room.adminRoles)
          : [];
        await syncGroup({
          ...base,
          roomId: room.roomId,
          name: room.name,
          kind: room.kind,
          participants: members,
          admins: admins.length ? admins : null,
        });
        live.add(room.roomId);
        continue;
      }

      prefixes.add(`${room.roomId}_`);
      for (const [key, group] of groupsFor(rows, room)) {
        const roomId = `${room.roomId}_${key}`;
        // Бүлэг нь байсаар байна — нэрээ хэн ч бөглөөгүй байсан ч өрөөг
        // амьд гэж үзнэ, эс бөгөөс ашиглаж буй өрөөний гишүүд хоосорно.
        live.add(roomId);
        if (!group.name) continue;
        await syncGroup({
          ...base,
          roomId: roomId,
          name: group.name,
          kind: room.kind,
          participants: group.members,
          admins: adminsAmong(
            room.adminRoles.length ? idsForRoles(rows, room.adminRoles) : [],
            group.members,
          ),
        });
      }
    }

    await retireVanishedRooms(existing, live, prefixes, writer);
    await writer.commit();
    console.log(
      `chat groups synced: config=${config.length} rooms=${live.size} me=${myId}`,
    );
    markDone();
    return;
  }

  await syncGroup({
    ...base,
    roomId: "school_all_staff",
    name: "Бүх ажилчид",
    kind: "staff",
    participants: staff,
    admins: leaders.length ? leaders : null,
  });
  await syncGroup({
    ...base,
    roomId: "school_all_teachers",
    name: "Бүх багш нар",
    kind: "teachers",
    participants: teachers,
    admins: null,
  });
  await syncGroup({
    ...base,
    roomId: "school_managers_teachers",
    name: "Менежер болон багш нар",
    kind: "teachers",
    participants: [...managers, ...teachers],
    admins: managers.length ? managers : null,
  });
  await syncGroup({
    ...base,
    roomId: "school_leaders",
    name: "Захирал, менежерүүд",
    kind: "staff",
    participants: leaders,
    admins: leaders.length ? leaders : null,
  });
  await syncGroup({
    ...base,
    roomId: "school_managers",
    name: "Менежерүүд",
    kind: "staff",
    participants: managers,
    admins: managers.length ? managers : null,
  });

  // Багшийн төрөл — аппын school_group_chats.dart-тай ижил өрөөний id.
  for (const [typeId, group] of byType) {
    if (!group.name) continue;
    await syncGroup({
      ...base,
      roomId: `school_type_${typeId}`,
      name: group.name,
      kind: "instructorType",
      participants: group.members,
      // Бүлгийн гишүүн менежер орж ирмэгц админ болно.
      admins: adminsAmong(managers, group.members),
    });
  }

  // Заах аргын нэгдэл.
  for (const [deptId, group] of byDepartment) {
    await syncGroup({
      ...base,
      roomId: `school_dept_${deptId}`,
      name: group.name,
      kind: "department",
      participants: group.members,
      admins: adminsAmong(managers, group.members),
    });
  }

  live.add("school_all_staff");
  live.add("school_all_teachers");
  for (const typeId of byType.keys()) live.add(`school_type_${typeId}`);
  for (const deptId of byDepartment.keys()) live.add(`school_dept_${deptId}`);
  await retireVanishedRooms(
    existing,
    live,
    new Set(["school_type_", "school_dept_"]),
    writer,
  );
  await writer.commit();

  markDone();
}

/// Глобал тохиргоо. Үнэхээр хоосон бол суурь жагсаалт хүчинтэй, харин
/// УНШИГДААГҮЙ бол null — энэ хоёрыг ялгахгүй бол түр зуурын алдаа гарахад
/// суурь жагсаалтаар бодоод тохиргоогоор нэмэгдсэн хүмүүсийг арчина.
async function loadRoomConfig() {
  try {
    const snap = await fb.db.collection("_auto_schoolchat_rooms").get();
    const rooms = [];
    snap.forEach((doc) => {
      const data = doc.data() || {};
      if (data.disabled === true) return;

      const roomId = String(data.room_id || "").trim();
      if (!roomId) return;

      const list = (key) =>
        Array.isArray(data[key])
          ? data[key].map((value) => String(value).trim()).filter(Boolean)
          : [];

      const filterColumn = list("filter_column");
      const name = String(data.name || "").trim();
      // Нэргүй тогтмол өрөө жагсаалтад хоосон харагдана.
      if (!filterColumn.length && !name) return;

      rooms.push({
        roomId: roomId,
        name: name,
        kind: String(data.kind || "").trim(),
        roles: list("roles"),
        adminRoles: list("admin_roles"),
        filterColumn: filterColumn,
        nameColumn: list("name_column"),
        joinAllRoles: list("join_all_roles").map((role) => role.toLowerCase()),
        order: typeof data.order === "number" ? data.order : 0,
      });
    });
    rooms.sort((a, b) => a.order - b.order);
    return rooms;
  } catch (e) {
    console.log("auto chat config read failed", e);
    return null;
  }
}

/// members дэд цуглуулгад бичих нэр — аппын ChatDirectory.staffName-тэй
/// ижил, эс бөгөөс нэг хүн аль тал сүүлд бичснээс шалтгаалж хоёр өөр нэртэй
/// болно. Жагсаалтад харуулдаг staffName нь товчилсон хэвээр.
/** Группын гишүүний баримт — аппын _memberDoc-той ижил талбарууд. */
function staffMemberDoc(school, doc, data, role, name) {
  return {
    id: doc.id,
    name: name,
    role: role,
    roleLabel: roleLabel(role),
    school: school.name || "",
    schoolId: school.ref.id,
    schoolPath: school.ref.path,
    schoolRef: school.ref,
    userRef: doc.ref,
    userPath: doc.ref.path,
    personId: data.PERSON_ID || doc.id,
    isGlobal: false,
  };
}

function memberName(data) {
  const display = String(data.DISPLAY_NAME || "").trim();
  if (display) return display;

  const first = String(data.FIRST_NAME || data.firstName || "").trim();
  const last = String(data.LAST_NAME || data.lastName || "").trim();
  if (!first && !last) return "-";
  return last && first ? `${last} ${first}` : `${last}${first}`;
}

/// Хоосон role жагсаалт нь бүгд гэсэн үг.
function idsForRoles(rows, roles) {
  const wanted = new Set(roles.map((role) => role.toLowerCase()));
  return Object.keys(rows).filter((id) => {
    const role = String(rows[id].role || "").toLowerCase();
    return !wanted.size || wanted.has(role);
  });
}

/// Тохируулсан баганы ялгаатай утга тус бүрд нэг өрөө.
function groupsFor(rows, room) {
  const groups = new Map();
  const everywhere = [];
  const wanted = new Set(room.roles.map((role) => role.toLowerCase()));

  for (const id of Object.keys(rows)) {
    const data = rows[id];
    const role = String(data.role || "").toLowerCase();

    // join_all_roles-д байхад л хангалттай: эдгээр хүн энэ тодорхойлолтын
    // үүсгэсэн БҮХ өрөөнд орно — захиралд өөрийн гэсэн ЗАН байдаггүй.
    if (room.joinAllRoles.includes(role)) {
      everywhere.push(id);
      continue;
    }
    if (wanted.size && !wanted.has(role)) continue;

    const parts = room.filterColumn.map((column) =>
      String(data[column] ?? "").trim(),
    );
    if (parts.some((value) => !value)) continue;

    let name = room.nameColumn
      .map((column) => String(data[column] ?? "").trim())
      .filter(Boolean)
      .join(" ");
    if (name.endsWith("багш")) name = `${name} нар`;

    const key = parts.join("_");
    if (!groups.has(key)) groups.set(key, { name: "", members: [] });
    const group = groups.get(key);
    if (!group.name && name) group.name = name;
    group.members.push(id);
  }

  for (const group of groups.values()) {
    group.members.push(...everywhere);
  }

  return groups;
}

async function syncGroup({
  school,
  people,
  existing,
  writer,
  myId,
  roomId,
  name,
  kind,
  participants,
  admins: rawAdmins,
  demoIds,
}) {
  const room = roomRef(school, roomId);
  const data = existing[roomId];
  // Демо бүртгэл тохиргооны adminRoles-оор ч админ болохгүй.
  const admins = rawAdmins
    ? rawAdmins.filter((id) => !demoIds.has(id))
    : null;

  // Гишүүдийн нэр, албан тушаал members дэд цуглуулгад — аймгийн чаттай
  // ижил бүтэц.
  const writeMembers = async (ids) => {
    for (const id of ids) {
      if (!people[id]) continue;
      await writer.set(room.collection("members").doc(id), people[id], true);
    }
  };

  if (!data) {
    if (participants.length < 2) return;
    await writer.set(room, {
      type: "group",
      name: name,
      participants: participants,
      userType: "teacher",
      createdAt: fb.firestore.FieldValue.serverTimestamp(),
      // Nobody owns an automatic room.
      createdBy: "system",
      admins:
        admins && admins.length
          ? admins
          : firstAdmin(participants, myId, demoIds),
      groupSettings: {
        isDefaultGroup: true,
        autoKind: kind,
        canMembersAddOthers: false,
        canMembersEditInfo: false,
      },
      deleted: false,
      membersSeeded: true,
    });
    await writeMembers(participants);
    return;
  }

  const update = {};
  const known = data.participants || [];
  const knownAdmins = data.admins || [];

  if (data.createdBy !== "system") update.createdBy = "system";

  // Гишүүд нь тохиргооны заасан хүмүүс. Сургуулиас гарсан, эсвэл role нь
  // тохиргооноос хасагдсан хүн гарна; тохиргоонд байгаа мөртлөө өрөөнөөс
  // алга болсон хүн — хуучин код арчсан захирал — хэн орсноос үл хамааран
  // буцаж нэмэгдэнэ.
  // Энэ өрөөнөөс тусад нь хассан хүн бүртгэлд байсан ч эргэж орохгүй.
  const excluded = data.excludedMembers || [];
  const wanted = excluded.length
    ? participants.filter((id) => !excluded.includes(id))
    : participants;
  const added = wanted.filter((id) => !known.includes(id));
  const removed = known.filter((id) => !wanted.includes(id));
  if (!sameMembers(known, wanted)) {
    update.participants = wanted;
    await writeMembers(added);
    for (const id of removed) {
      await writer.delete(room.collection("members").doc(id));
    }
  }

  // Сургууль авто өрөөний нэрийг өөрөө сольж болно; `nameChanged` нь түүнийг
  // хэлэх тэмдэг бөгөөд түүнээс хойш тохиргоо нэрийг нь хөндөхгүй.
  if (data.nameChanged !== true && data.name !== name) update.name = name;

  const settings = data.groupSettings || {};
  if (settings.autoKind !== kind) update["groupSettings.autoKind"] = kind;

  // Тохиргооны role-оор заасан хүн одоо байгаа админууд дээр НЭМЭГДЭНЭ:
  // дараа бүртгэгдсэн менежер орж ирэхдээ админ болох ба түүнийг хүртэл
  // өрөөг хөтөлсөн админ хэвээр үлдэнэ.
  //
  // Гишүүн биш болсон админыг хасна: өөр нэгдэл рүү шилжсэн багш хуучин
  // өрөөнийхөө админаар үлдэж, өрөө нь эзэнгүй болж байсан. Авто өрөө
  // админгүй үлдэхгүй — аппын school_group_chats.dart-тай ижил дүрэм.
  const kept = knownAdmins.filter(
    (id) => wanted.includes(id) && !demoIds.has(id),
  );
  const next = kept.concat(
    (admins || []).filter((id) => !kept.includes(id) && wanted.includes(id)),
  );
  const ensured = next.length ? next : firstAdmin(wanted, myId, demoIds);
  if (!sameMembers(knownAdmins, ensured)) update.admins = ensured;

  // Seed нэг л удаа.
  if (data.membersSeeded !== true) {
    await writeMembers(participants);
    update.membersSeeded = true;
  }

  if (Object.keys(update).length) await writer.update(room, update);
}

/// Бүлэг нь бүрмөсөн алга болсон авто өрөө хэнд ч харагдахаа болино —
/// мессежийг устгахгүйгээр гишүүдийг нь хоослоно.
async function retireVanishedRooms(existing, live, prefixes, writer) {
  for (const [roomId, data] of Object.entries(existing)) {
    const settings = data.groupSettings || {};
    if (settings.isDefaultGroup !== true) continue;

    const generated = [...prefixes].some((prefix) => roomId.startsWith(prefix));
    if (!generated || live.has(roomId)) continue;
    if (!(data.participants || []).length) continue;

    await writer.update(writer.roomRef(roomId), { participants: [] });
    for (const id of data.participants || []) {
      await writer.delete(writer.roomRef(roomId).collection("members").doc(id));
    }
  }
}

/// Firestore нэг batch-д 500 бичилт авдаг тул дүүрэхэд нь commit хийгээд
/// шинийг эхлүүлнэ. Хурднаас гадна нэг commit нь нэг stream event —
/// өрөө тутамд тусад нь бичихэд чатын жагсаалт дахин дахин зурагдана.
function makeWriter(school) {
  let batch = null;
  let ops = 0;

  const commit = async () => {
    if (!batch) return;
    const pending = batch;
    batch = null;
    ops = 0;
    await pending.commit();
  };

  const added = async () => {
    ops += 1;
    if (ops >= 450) await commit();
  };

  return {
    roomRef: (roomId) => roomRef(school, roomId),
    set: async (ref, data, merge) => {
      batch = batch || fb.db.batch();
      if (merge) batch.set(ref, data, { merge: true });
      else batch.set(ref, data);
      await added();
    },
    update: async (ref, data) => {
      batch = batch || fb.db.batch();
      batch.update(ref, data);
      await added();
    },
    delete: async (ref) => {
      batch = batch || fb.db.batch();
      batch.delete(ref);
      await added();
    },
    commit,
  };
}

/** Өрөө админгүй үлдэж болохгүй: нээсэн хүн гишүүн бол тэр, эс бөгөөс
 *  id-гаар эрэмбэлсэн эхний жинхэнэ (демо биш) гишүүн. */
function firstAdmin(participants, myId, demoIds) {
  if (participants.includes(myId)) return [myId];
  const real = participants.filter((id) => !demoIds.has(id)).sort();
  return real.length ? [real[0]] : [];
}

/** Бүлгийн гишүүн болох удирдлагууд — гишүүн биш хүнийг админ болгохгүй. */
function adminsAmong(candidates, members) {
  const picked = candidates.filter((id) => members.includes(id));
  return picked.length ? picked : null;
}

function sameMembers(a, b) {
  return a.length === b.length && b.every((id) => a.includes(id));
}

/** Everyone in a room, named — for the members list of a group. */
export async function loadMembers(school, year, room) {
  const admins = room.admins || [];
  let members = [];

  // National rooms span every school, so their people come from `members`.
  if (isNationalId(room.id)) {
    const snap = await roomRef(school, room.id).collection("members").get();
    snap.forEach((doc) => {
      const data = doc.data() || {};
      members.push({
        id: doc.id,
        name: data.name || "Хэрэглэгч",
        role: String(data.role || "").toLowerCase(),
        label: [data.roleLabel, data.school].filter(Boolean).join(" · "),
        school: data.school || "",
        photo: data.photoURL || "",
        isAdmin: admins.includes(doc.id),
      });
    });
  } else {
    const info = await loadPeopleInfo(school, year, room.participants || []);
    const names = room.participantNames || {};
    members = (room.participants || []).map((id) => {
      const known = info[id] || {};
      return {
        id: id,
        name: known.name || names[id] || "Хэрэглэгч",
        role: known.role || "",
        label: known.label || "",
        photo: known.photo || "",
        isAdmin: admins.includes(id) || room.createdBy === id,
      };
    });
  }

  members.sort((a, b) => {
    if (a.isAdmin !== b.isAdmin) return a.isAdmin ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
  return members;
}

// ── People to chat with ─────────────────────────────────────────────────────

/** Everyone working at the school, excluding the reader. */
export async function loadStaff(school, myId) {
  const snap = await school.ref.collection("teachers").get();
  const people = [];

  snap.forEach((doc) => {
    const data = doc.data() || {};
    if (doc.id === myId) return;
    if (data.deleted === true || data.moved === true) return;

    const name = staffName(data);
    const role = String(data.role || "").toLowerCase();
    // Class-assignment stubs carry neither a name nor a role.
    if (name === "" && role === "") return;

    people.push({
      id: doc.id,
      name: name || "Ажилтан",
      subtitle: data.POSITION_NAME || roleLabel(role),
      userType: "teacher",
      photo: data.photoURL || "",
    });
  });

  people.sort((a, b) => a.name.localeCompare(b.name));
  return people;
}

/** Pupils of one class, or their parents. */
export async function loadClassMembers(school, year, classGroupId, asParents) {
  const snap = await school.ref
    .collection(`students-${year}`)
    .where("STUDENT_GROUP_ID", "==", Number(classGroupId) || classGroupId)
    .get();

  const people = [];
  snap.forEach((doc) => {
    const data = doc.data() || {};
    if (data.deleted === true) return;

    const name = studentName(data);
    people.push({
      id: asParents ? `parent_${doc.id}` : doc.id,
      name: asParents ? `${name} — эцэг эх` : name,
      subtitle: data.STUDENT_GROUP_NAME || "",
      userType: asParents ? "parent" : "student",
      photo: (asParents ? data.PARENT_PHOTO_URL : data.photoURL) || "",
    });
  });

  people.sort((a, b) => a.name.localeCompare(b.name));
  return people;
}

export async function loadClasses(school, year) {
  const snap = await school.ref.collection(`classGroups-${year}`).get();
  const classes = [];

  snap.forEach((doc) => {
    const data = doc.data() || {};
    if (data.deleted === true) return;
    const name = data.STUDENT_GROUP_NAME;
    if (!name) return;
    classes.push({
      id: doc.id,
      name: name,
      level: Number(data.ACADEMIC_LEVEL) || 0,
    });
  });

  classes.sort((a, b) => a.level - b.level || a.name.localeCompare(b.name));
  return classes;
}

// ── Small helpers ───────────────────────────────────────────────────────────

export function staffName(data) {
  const display = String(data.DISPLAY_NAME || "").trim();
  if (display) return display;

  const first = String(data.FIRST_NAME || data.firstName || "").trim();
  const last = String(data.LAST_NAME || data.lastName || "").trim();
  if (first && last) return `${last[0]}.${first}`;
  return first || last || "";
}

/// Овгийн эхний үсэг, нэр — багш нарын нэртэй ижил хэлбэр, аппынхтай нэг.
export function studentName(data) {
  const first = String(data.FIRST_NAME || data.firstName || "").trim();
  const last = String(data.LAST_NAME || data.lastName || "").trim();
  if (first && last) return `${last[0]}.${first}`;
  return first || last || "-";
}

export function roleLabel(role) {
  const labels = {
    teacher: "Багш",
    schooldirector: "Захирал",
    schoolmanager: "Менежер",
    schooladmin: "Админ",
    schoolaccountant: "Нягтлан",
    schoolmealcontroller: "Хоол зүйч",
    schooldoctor: "Эмч",
  };
  return labels[role] || (role ? role : "Ажилтан");
}

/** The other side of a one-to-one room. */
export function otherName(room, myId, info) {
  if (room.type === "group") return room.name || "Групп чат";

  const otherId = otherParticipant(room, myId);
  const fromRoom = (room.participantNames || {})[otherId];
  const known = info ? info[otherId] : null;

  // The roster search finished and found nobody — the account was deleted.
  if (known && known.missing) return fromRoom || "Устсан хэрэглэгч";

  // Rooms the mobile app created are simply called "Direct Chat", so the
  // roster is what actually names the person.
  return (known && known.name) || fromRoom || "Чат";
}

/** The picture of the other side of a one-to-one room, when they set one. */
export function otherPhoto(room, myId, info) {
  if (room.type === "group") return "";
  const known = info ? info[otherParticipant(room, myId)] : null;
  return (known && known.photo) || "";
}

export function otherParticipant(room, myId) {
  return (room.participants || []).find((id) => id !== myId) || "";
}

/**
 * Names for a set of ids: staff by document id, pupils by document id and
 * their parents as "parent_<id>". Read once and kept, so the list does not
 * hit Firestore per row.
 */
export async function loadNames(school, year, ids) {
  const names = {};
  const wanted = [...new Set(ids.filter(Boolean))];
  if (!wanted.length) return names;

  const studentIds = wanted
    .filter((id) => id.startsWith("parent_"))
    .map((id) => id.replace("parent_", ""));

  const staffSnap = await school.ref.collection("teachers").get();
  staffSnap.forEach((doc) => {
    if (!wanted.includes(doc.id)) return;
    names[doc.id] = staffName(doc.data() || {}) || "Ажилтан";
  });

  const missing = wanted.filter(
    (id) => !names[id] && !id.startsWith("parent_"),
  );
  const lookFor = [...new Set([...missing, ...studentIds])];

  for (let i = 0; i < lookFor.length; i += 10) {
    const batch = lookFor.slice(i, i + 10);
    if (!batch.length) continue;

    const snap = await school.ref
      .collection(`students-${year}`)
      .where(fb.firestore.FieldPath.documentId(), "in", batch)
      .get();

    snap.forEach((doc) => {
      const name = studentName(doc.data() || {});
      names[doc.id] = name;
      names[`parent_${doc.id}`] = `${name} — эцэг эх`;
    });
  }

  return names;
}

/**
 * What the list shows under the name. Rooms written before the delete text
 * was made readable still hold "<id>|<name>:DELETED_MESSAGE".
 */
export function previewText(room) {
  const text = String(room.lastMessage || "");
  if (text.endsWith(":DELETED_MESSAGE")) return "Устгагдсан мессеж";
  return text;
}

/**
 * Small labels on a row. A group says what kind of group it is; a one-to-one
 * chat says who the other person is — their role, or their class when it is
 * a pupil or a parent.
 */
export function roomChips(room, myId, info) {
  const chips = [];

  if (room.type !== "group") {
    const known = info ? info[otherParticipant(room, myId)] : null;
    if (known && known.label) chips.push(known.label);
    if (room.userType === "parent") chips.push("Эцэг эх");
    return chips;
  }

  if (room.type === "group") {
    chips.push("Групп");

    const auto = (room.groupSettings || {}).autoKind;
    if (auto === "staff") chips.push("Ажилчдын");
    else if (auto === "teachers") chips.push("Багш нарын");
    else if (auto === "instructorType") chips.push("Багшийн төрөл");
    else if (auto === "department") chips.push("Заах аргын нэгдэл");
    else if (room.userType === "student") chips.push("Ангийн");
    else if (room.userType === "parent") chips.push("Эцэг эхийн");

    return chips;
  }

  return chips;
}

export function millis(timestamp) {
  if (!timestamp) return 0;
  if (timestamp.toDate) return timestamp.toDate().getTime();
  if (timestamp instanceof Date) return timestamp.getTime();
  return 0;
}

/// Бүх тэмдэг нэг хэлбэртэй: «8-р сарын 26, 16:12» — аппынхтай ижил.
export function stampLabel(timestamp) {
  const ms = millis(timestamp);
  if (!ms) return "";

  const time = new Date(ms);
  const pad = (n) => String(n).padStart(2, "0");
  const clock = `${pad(time.getHours())}:${pad(time.getMinutes())}`;
  const day = `${time.getMonth() + 1}-р сарын ${time.getDate()}`;

  if (time.getFullYear() !== new Date().getFullYear()) {
    return `${time.getFullYear()} оны ${day}, ${clock}`;
  }
  return `${day}, ${clock}`;
}

/// Өмнөх мессежээс өдөр нь өөр эсвэл 60 минут зөрсөн үед л тэмдэг гарна.
export function needsStamp(message, older) {
  const at = millis(message && message.timestamp);
  if (!at) return false;
  const before = millis(older && older.timestamp);
  if (!before) return true;

  const a = new Date(at);
  const b = new Date(before);
  const sameDay =
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  return !sameDay || (at - before) / 60000 >= 60;
}

const URL_PATTERN =
  /(https?:\/\/[^\s]+|www\.[^\s]+|[\w-]+\.(?:mn|com|org|net|edu|info|io)(?:\/[^\s]*)?)/gi;

/// Мессежийн текстийг энгийн хэсэг, линк болгон хуваана — аппын
/// _urlPattern-тэй ижил хэв.
export function linkifyParts(content) {
  const text = String(content || "");
  const parts = [];
  let index = 0;
  let match;

  URL_PATTERN.lastIndex = 0;
  while ((match = URL_PATTERN.exec(text)) !== null) {
    if (match.index > index) {
      parts.push({ text: text.slice(index, match.index), href: null });
    }
    const url = match[0];
    parts.push({
      text: url,
      href: /^https?:\/\//i.test(url) ? url : `https://${url}`,
    });
    index = match.index + url.length;
  }
  if (index < text.length) parts.push({ text: text.slice(index), href: null });
  return parts;
}

/** linkifyParts plus each tagged "@Name" marked; whether it is me goes by id, as in the app. */
export function messageParts(content, mentions, myId) {
  const tags = (mentions || []).filter((m) => m && m.name);
  const out = [];
  for (const part of linkifyParts(content)) {
    if (part.href || !tags.length) {
      out.push(part);
      continue;
    }
    let text = part.text;
    while (text) {
      let hit = null;
      let at = -1;
      for (const m of tags) {
        const i = text.indexOf("@" + m.name);
        if (i >= 0 && (at < 0 || i < at)) {
          at = i;
          hit = m;
        }
      }
      if (!hit) {
        out.push({ text, href: null });
        break;
      }
      if (at > 0) out.push({ text: text.slice(0, at), href: null });
      const tag = "@" + hit.name;
      out.push({ text: tag, href: null, mention: true, me: hit.id === myId });
      text = text.slice(at + tag.length);
    }
  }
  return out;
}

/** Saves a Storage file to the computer; needs the bucket's CORS (scripts/setStorageCors.js). */
export async function downloadFile(url) {
  // The <img> on screen cached it without CORS headers; skip that copy.
  const response = await fetch(url, { cache: "no-store" });
  if (!response.ok) throw new Error(String(response.status));
  const blob = await response.blob();
  const path = decodeURIComponent(String(url).split("/o/")[1] || "").split("?")[0];
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = path.split("/").pop() || "zurag.jpg";
  link.click();
  URL.revokeObjectURL(link.href);
}

export function timeLabel(timestamp) {
  const ms = millis(timestamp);
  if (!ms) return "";

  const date = new Date(ms);
  const pad = (n) => String(n).padStart(2, "0");
  const today = new Date();
  const sameDay =
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate();

  if (sameDay) return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
  return `${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}
